import type { CapturedNetworkRequest, SessionRecordingOptions } from 'posthog-js';

import { scrubNetworkPayload } from './posthog-network-payload-scrub';

import { scrubSensitiveUrlParams } from '@/lib/analytics/sensitive-url-params';

/**
 * What PostHog session replays leave out. The recorder (rrweb) replaces every
 * non-whitespace character with `*` in input values and in text under any
 * element matching `maskTextSelector`, so replays keep the layout, navigation
 * and clicks without customer code, chat text, or anything users type.
 */

/**
 * Marks a container whose text replays must not show, such as a conversation
 * or a composer. Spread {@link SESSION_REPLAY_MASK_PROPS} onto its top-level
 * element; all text under it in the light DOM is masked.
 */
export const SESSION_REPLAY_MASK_ATTRIBUTE = 'data-replay-mask';

export const SESSION_REPLAY_MASK_PROPS = { [SESSION_REPLAY_MASK_ATTRIBUTE]: '' } as const;

/**
 * Leaves an element out of replays entirely, keeping only its size: masking
 * hides text, not the pixels of an image, such as a screenshot a user
 * attached. PostHog's own default block class, set here so it cannot drift.
 */
export const SESSION_REPLAY_BLOCK_CLASS = 'ph-no-capture';

const MASK_TEXT_SELECTOR = [
  `[${SESSION_REPLAY_MASK_ATTRIBUTE}]`,
  // Code: Markdown code blocks, and the lines @pierre/diffs renders inside each
  // diff's shadow root. rrweb looks for a masked ancestor with closest(), which
  // stops at a shadow root, so no marker outside a diff reaches the lines Pierre
  // adds or re-highlights after the first snapshot. Pierre's <pre> is inside.
  'pre',
  // Input masking covers input, textarea and select values, not text typed into
  // an editable element.
  '[contenteditable]:not([contenteditable="false"])',
].join(', ');

// rrweb masks text nodes, not attributes. Markdown code blocks copy their
// source into `data-code-content`, Markdown links and images carry message
// content in `href`, `src` and `alt`, and some chat controls repeat a prompt in
// their accessible name.
const TEXT_ATTRIBUTES = new Set(['aria-label', 'title', 'data-code-content', 'alt', 'href', 'src']);

function maskAttribute(name: string, value: string, element?: Element): string {
  if (!TEXT_ATTRIBUTES.has(name) || !element?.closest(MASK_TEXT_SELECTOR)) {
    return value;
  }
  return value.replace(/\S/g, '*');
}

/**
 * The recorder passes every URL a replay stores through this hook: the page URL of each full
 * snapshot, `$url_changed`, the replay copy of `$pageview`, and each captured network request. It
 * drops a page URL entry when the hook returns nothing, so this always returns the entry.
 */
function maskCapturedNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return {
    ...request,
    name: scrubSensitiveUrlParams(request.name),
    requestBody: scrubNetworkPayload(request.requestBody, 'Request'),
    responseBody: scrubNetworkPayload(request.responseBody, 'Response'),
  };
}

/** PostHog `session_recording` masking options. */
export const SESSION_REPLAY_MASKING = {
  blockClass: SESSION_REPLAY_BLOCK_CLASS,
  maskAllInputs: true,
  maskTextSelector: MASK_TEXT_SELECTOR,
  maskAttributeFn: maskAttribute,
  maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
  // Replays keep request timing but not payloads: API responses carry the same
  // code and chat text the page masks, and headers can carry credentials.
  // `false` here overrides the network payload setting in PostHog's project
  // settings.
  recordBody: false,
  recordHeaders: false,
} satisfies SessionRecordingOptions;
